import * as React from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle, faInfoCircle, faExclamationCircle,
} from '@fortawesome/free-solid-svg-icons';
import COLOR, { transparent } from 'constants/color';

type Status = 'error' | 'success' | 'info';

const statusColors: { [key: string]: keyof typeof COLOR } = {
  success: 'cyan',
  error: 'red',
  info: 'grayishBlue',
};

const statusIcons = {
  success: faCheckCircle,
  error: faExclamationCircle,
  info: faInfoCircle,
};

interface AlertProps {
  display?: 'row' | 'column';
  status?: Status;
  children: React.ReactNode;
  noBackground?: boolean;
}

type Props = AlertProps & React.HTMLAttributes<HTMLDivElement>;

const Alert = ({ children, status = 'info', noBackground, ...alertProps }: Props): JSX.Element => {
  return (
    <AlertStyle
      role="alert"
      status={status}
      noBackground={noBackground}
      {...alertProps}
    >
      <IconContainer status={status}>
        <AlertInfoIcon color={COLOR[statusColors[status]]} icon={statusIcons[status]} />
      </IconContainer>
      <BodyContainer
        status={status}
        noBackground={noBackground}
      >
        {children}
      </BodyContainer>
    </AlertStyle>
  );
};

const AlertStyle = styled.div<AlertProps>`
  display: flex;
  align-items: flex-start;
  border-radius: 0.75rem;
  ${(props): string => props.noBackground ? '' : `border: 1px solid ${COLOR[statusColors[props.status]]}};`}
  ${(props): string => props.noBackground ? '' : 'padding: 1rem;'}
  position: relative;
  flex-direction: ${(props): string => props.display ? props.display : 'column'};
  background: ${(props): string => props.noBackground ? 'none' : transparent(statusColors[props.status], 0.2)};
`;

const IconContainer = styled.span<AlertProps>`
  color: ${(props): string => COLOR[statusColors[props.status]]};
  font-size: 0.875rem;
  margin-bottom: 1rem;
`;

const AlertInfoIcon = styled(FontAwesomeIcon)<{ color: string }>`
  color: ${(props): string => props.color};
  font-size: 1rem;
  margin-right: 0.3rem;
`;

const BodyContainer = styled.div<{ status: Status; noBackground?: boolean }>`
  ${(props): string => props.noBackground ? `color: ${COLOR[statusColors[props.status]]};` : ''}
  font-size: 0.875rem;
`;

export default Alert;
